/**
 * Personal listener для обновления статуса МОЕГО dream на главной
 * Срабатывает только для событий scope: personal (мой dream)
 */

import {
  getDreamControllerGetMyQueryKey,
  getDreamControllerListDonationsQueryKey,
} from '@/api/dreams/dreams'
import { getUserControllerGetMeQueryKey } from '@/api/users/users'
import { client } from '@/features/query/client'
import {
  DonationConfirmedPayloadDto,
  DonationReceivedPayloadDto,
  SseEventDtoEvent,
} from '@/api/model'
import { Listeners } from 'src/api/sse'
import { debounce } from 'lodash'

// Debounced функции для объединения множественных инвалидаций
// Если приходит несколько событий подряд - выполнится только один запрос
const debouncedInvalidateMyDream = debounce(
  () => {
    client.invalidateQueries({ queryKey: getDreamControllerGetMyQueryKey() })
  },
  500,
  { leading: false, trailing: true },
)

const debouncedInvalidateMyDreamAndUser = debounce(
  () => {
    client.invalidateQueries({ queryKey: getDreamControllerGetMyQueryKey() })
    client.invalidateQueries({ queryKey: getUserControllerGetMeQueryKey() })
  },
  500,
  { leading: false, trailing: true },
)

// Универсальный handler для всех personal событий изменения статуса dream
const invalidateMyDream = () => {
  debouncedInvalidateMyDream()
}

// Handler для закрытия/удаления dream (когда hasActiveDream меняется)
const invalidateMyDreamAndUser = () => {
  debouncedInvalidateMyDreamAndUser()
}

// Handler для donation.received события - инвалидируем dream и список донатов
const invalidateOnDonationReceived: Listeners['donation.received'][number] = (
  event,
) => {
  const data = event.data as { payload?: DonationReceivedPayloadDto }
  const dreamId = data?.payload?.dreamId

  debouncedInvalidateMyDream()

  if (dreamId) {
    client.invalidateQueries({
      queryKey: getDreamControllerListDonationsQueryKey(dreamId),
    })
  }
}

// Handler для donation.confirmed события
const invalidateOnDonationConfirmed: Listeners['donation.confirmed'][number] = (
  event,
) => {
  const data = event.data as { payload?: DonationConfirmedPayloadDto }
  const dreamId = data?.payload?.dreamId

  debouncedInvalidateMyDream()

  if (dreamId) {
    client.invalidateQueries({
      queryKey: getDreamControllerListDonationsQueryKey(dreamId),
    })
  }
}

// Экспортируем listeners для регистрации в SSE Provider
export const personalDreamStatusListeners: {
  [K in keyof Listeners]?: Listeners[K][number]
} = {
  // События изменения статуса (hasActiveDream остается true)
  [SseEventDtoEvent.dreamfrozen]: invalidateMyDream,
  [SseEventDtoEvent.dreamunfrozen]: invalidateMyDream,
  [SseEventDtoEvent.dreamapproved]: invalidateMyDream,
  [SseEventDtoEvent.dreamrejected]: invalidateMyDream,
  [SseEventDtoEvent.dreamgoal_reached]: invalidateMyDream,

  // События когда dream закрывается (hasActiveDream меняется на false)
  [SseEventDtoEvent.dreamclosed]: invalidateMyDreamAndUser,
  [SseEventDtoEvent.dreamdeleted]: invalidateMyDreamAndUser,

  // События донатов - обновляем прогресс сбора и список донатов
  [SseEventDtoEvent.donationreceived]: invalidateOnDonationReceived,
  [SseEventDtoEvent.donationconfirmed]: invalidateOnDonationConfirmed,
} as const
